import { decamelizeKeys } from 'humps';

import { apiClient } from '../apiClient';

export const PATHS = {
  COACH_MATCHING: '/coach_matching/',
  PRIMARY_COACH: (teamCode: string) => `/api/v4/team/${teamCode}/primary_coach/`,
  COACH_AVAILABILITY: '/calendaring/api/v3/coach_availability',
  SCHEDULED_CALL: '/api/v3/scheduledcall/',
} as const;

export type CoachLicense = { state: string; type: string; number: string };

// A coach returned by /coach_matching/ — the subset of Sparta's `Provider`
// (the /api/v3/coachprofile shape) the matching screens render.
export type MatchedCoach = {
  uuid: string;
  displayName: string;
  // The coach's first name — the fallback addressing (firstName + title) when
  // displayName is empty.
  firstName: string;
  title: string;
  coachType: string;
  image: string | null;
  bio: string | null;
  quote: string | null;
  qualifications: string[];
  specialties: string[];
  licenses: CoachLicense[];
  // The coach's USER uuid — what coach-availability is keyed by, distinct from
  // the coach-profile `uuid` used for primary-coach assignment.
  userUuid: string;
  // The coach-profile resource uri (/api/v3/coachprofile/<uuid>/).
  resourceUri: string;
  // The coach's USER resource uri (/api/v3/user/<uuid>/) — this is what
  // `scheduledcall` expects for `coach` (Sparta: selectedCoach.user.resourceUri),
  // NOT the coachprofile resourceUri.
  userResourceUri: string;
};

export type MatchedCoaches = {
  batchUuid?: string;
  coaches: MatchedCoach[];
};

// The wire Provider shape (camelized by the response interceptor). Only the
// fields we map are typed; the wire carries more.
type ProviderWire = {
  uuid?: string;
  providerDisplayName?: string;
  coachType?: string;
  longDescription?: string;
  quote?: string;
  largeImage?: string;
  resourceUri?: string;
  user?: {
    coachTitle?: string;
    firstName?: string;
    uuid?: string;
    resourceUri?: string;
    image?: { url?: string };
  };
  licenses?: { state?: string; licenseType?: string; licenseNumber?: string }[];
  qualifications?: { name?: string }[];
  specialties?: { name?: string }[];
};

// PUT /coach_matching/ responds with `{ batchUuid?, objects: Provider[] }` —
// either bare (the live shape) or array-wrapped (Sparta's typing).
type CoachMatchingWrapper = { batchUuid?: string; objects?: ProviderWire[] };
type CoachMatchingResponse = CoachMatchingWrapper | CoachMatchingWrapper[];

function toMatchedCoach(provider: ProviderWire): MatchedCoach {
  return {
    uuid: provider.uuid ?? '',
    displayName: provider.providerDisplayName ?? '',
    firstName: provider.user?.firstName ?? '',
    title: provider.user?.coachTitle ?? '',
    coachType: provider.coachType ?? '',
    image: provider.user?.image?.url ?? provider.largeImage ?? null,
    bio: provider.longDescription ?? null,
    quote: provider.quote ?? null,
    qualifications: (provider.qualifications ?? []).map((q) => q.name ?? '').filter(Boolean),
    specialties: (provider.specialties ?? []).map((s) => s.name ?? '').filter(Boolean),
    licenses: (provider.licenses ?? []).map((l) => ({
      state: l.state ?? '',
      type: l.licenseType ?? '',
      number: l.licenseNumber ?? '',
    })),
    userUuid: provider.user?.uuid ?? '',
    resourceUri: provider.resourceUri ?? '',
    userResourceUri:
      provider.user?.resourceUri ??
      (provider.user?.uuid ? `/api/v3/user/${provider.user.uuid}/` : ''),
  };
}

// Mirrors Sparta's matchWithCoach: PUT /coach_matching/ keyed by the customer
// resource uri. The backend auto-matches coaches (no preference inputs) and
// returns them plus a batchUuid that primary-coach assignment echoes back.
export async function fetchMatchedCoaches(
  customerUri: string,
  signal?: AbortSignal
): Promise<MatchedCoaches> {
  const res = await apiClient.put<CoachMatchingResponse>(
    PATHS.COACH_MATCHING,
    decamelizeKeys({ customerUri }),
    { signal }
  );
  // The endpoint returns a bare `{ batchUuid, objects }` object; tolerate an
  // array-wrapped variant too (Sparta types it that way).
  const wrapper = Array.isArray(res.data) ? res.data[0] : res.data;
  return {
    batchUuid: wrapper?.batchUuid,
    coaches: (wrapper?.objects ?? []).map(toMatchedCoach),
  };
}

// Mirrors Sparta's setPrimaryCoachForTeam: POST /api/v4/team/{code}/primary_coach/.
// Assigns the chosen coach as the solo team's primary coach (completes coach
// matching → the user now `hasCoach`).
export async function setPrimaryCoachForTeam(
  args: { teamCode: string; coachUuid: string; batchUuid?: string },
  signal?: AbortSignal
): Promise<void> {
  await apiClient.post(
    PATHS.PRIMARY_COACH(args.teamCode),
    decamelizeKeys({
      coachUuid: args.coachUuid,
      batchUuid: args.batchUuid,
      teamCode: args.teamCode,
    }),
    { signal }
  );
}

export type CoachAvailability = {
  // Consult length in minutes.
  duration: number;
  // Available start times as ISO timestamps.
  timeslots: string[];
};

// Mirrors Sparta's getCoachAvailability: GET /calendaring/api/v3/coach_availability/{uuid}.
export async function fetchCoachAvailability(
  providerUuid: string,
  signal?: AbortSignal
): Promise<CoachAvailability> {
  const res = await apiClient.get<Partial<CoachAvailability>>(
    `${PATHS.COACH_AVAILABILITY}/${providerUuid}`,
    { params: decamelizeKeys({ callType: 'initial-consult' }), signal }
  );
  return { duration: res.data?.duration ?? 0, timeslots: res.data?.timeslots ?? [] };
}

// Mirrors Sparta's createScheduledCalls: POST /api/v3/scheduledcall/. Books the
// initial consult with the chosen coach at the selected ISO timeslot.
export async function createScheduledCall(
  args: { coach: string; date: string },
  signal?: AbortSignal
): Promise<void> {
  await apiClient.post(
    PATHS.SCHEDULED_CALL,
    decamelizeKeys({ callType: 'initial-consult', date: args.date, coach: args.coach }),
    { signal }
  );
}
